export type DepthRange = [number, number];

export type DepthPlotGeometry = {
  top: number;
  height: number;
  plotTop?: number;
  plotBottom?: number;
};

export const isFiniteDepthValue = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export function normalizeDepths(depths: number[]): number[] {
  return [...new Set(depths.filter(isFiniteDepthValue))].sort((a, b) => a - b);
}

export function findNearestDepth(depths: number[], target: number): number | null {
  if (!depths.length || !Number.isFinite(target)) return null;
  let low = 0;
  let high = depths.length - 1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const value = depths[mid];
    if (value === target) return value;
    if (value < target) low = mid + 1;
    else high = mid - 1;
  }
  if (low >= depths.length) return depths[depths.length - 1];
  if (high < 0) return depths[0];
  return Math.abs(depths[low] - target) < Math.abs(depths[high] - target)
    ? depths[low]
    : depths[high];
}

export function findNearestByDepth<T>(
  items: T[],
  target: number | null,
  getDepth: (item: T) => number,
): T | null {
  if (!items.length || target == null || !Number.isFinite(target)) return null;
  let nearest: T | null = null;
  let distance = Number.POSITIVE_INFINITY;
  items.forEach((item) => {
    const depth = getDepth(item);
    if (!Number.isFinite(depth)) return;
    const nextDistance = Math.abs(depth - target);
    if (nextDistance < distance) {
      nearest = item;
      distance = nextDistance;
    }
  });
  return nearest;
}

export function getMinimumDepthSpan(depths: number[], fullSpan: number): number {
  if (depths.length < 2 || fullSpan <= 0) return fullSpan;
  let minimumGap = Number.POSITIVE_INFINITY;
  for (let index = 1; index < depths.length; index += 1) {
    const gap = depths[index] - depths[index - 1];
    if (gap > 0 && gap < minimumGap) minimumGap = gap;
  }
  const pointSpan = Number.isFinite(minimumGap) ? minimumGap * 2 : fullSpan;
  return Math.min(fullSpan, Math.max(pointSpan, fullSpan * 0.01));
}

export function clampDepthRange(
  range: DepthRange,
  fullRange: DepthRange,
  minimumSpan: number,
): DepthRange {
  const [fullMin, fullMax] = fullRange;
  const fullSpan = Math.max(0, fullMax - fullMin);
  if (fullSpan === 0) return [fullMin, fullMax];

  let start = Math.min(range[0], range[1]);
  let end = Math.max(range[0], range[1]);
  const span = Math.min(fullSpan, Math.max(minimumSpan, end - start));
  const center = (start + end) / 2;
  start = center - span / 2;
  end = center + span / 2;
  if (start < fullMin) {
    end += fullMin - start;
    start = fullMin;
  }
  if (end > fullMax) {
    start -= end - fullMax;
    end = fullMax;
  }
  return [Math.max(fullMin, start), Math.min(fullMax, end)];
}

export function clientYToDepth(
  clientY: number,
  range: DepthRange,
  geometry: DepthPlotGeometry,
): number {
  const plotTop = geometry.plotTop ?? 0;
  const plotBottom = geometry.plotBottom ?? 0;
  const plotHeight = Math.max(1, geometry.height - plotTop - plotBottom);
  const fraction = Math.max(0, Math.min(1, (clientY - geometry.top - plotTop) / plotHeight));
  return range[0] + fraction * (range[1] - range[0]);
}
